/**
 * Field anchor → viewer document-space conversion.
 *
 * The backend stores each {@link Field}'s position in `anchor`, a JSONB blob
 * shaped like `{page: number | null, rect: [x1,y1,x2,y2] | null, ...}`.
 * For PDFs, `rect` is in PDF user-space units (origin bottom-left). The
 * viewer normalises pages via pdf.js's `getViewport({scale: 1, rotation: 0})`
 * and stores width/height in `PageModel.intrinsic` — pdf.js's scale=1
 * convention is "1 unit = 1 PDF user-unit", so `intrinsic` is in the same
 * coordinate system as the rect, just with origin at the top-left.
 *
 * Concretely: this module just flips the Y axis. `documentRectToView`
 * (from `viewer/coordinates`) handles the scale + rotation transform on
 * top of the flipped rect, so overlay layers get correct positioning
 * across zoom and rotation for free.
 *
 * Image / DOCX documents don't carry a per-field rect today (the anchor
 * only carries `{page}` or `{source: 'MERGEFIELD', ...}`), so this helper
 * returns null and overlay code falls back to a non-positional indicator.
 */
import type { PageDimensions } from './viewer/types'
import type { Rect } from './viewer/coordinates'

export interface FieldAnchor {
  /** 0-based page index. May be null for "document-level" fields. */
  page?: number | null
  /** PDF user-space [x1, y1, x2, y2] (points, origin bottom-left). */
  rect?: readonly number[] | null
  /** Free-form extractor metadata; we only consume `page` and `rect`. */
  [key: string]: unknown
}

/**
 * Read the page index off an anchor, normalising the wire shape. Returns
 * null when the anchor is missing or the page is not pinned.
 */
export function anchorPageIndex(anchor: FieldAnchor | null | undefined): number | null {
  if (!anchor) return null
  const page = anchor.page
  if (typeof page !== 'number') return null
  if (!Number.isFinite(page) || page < 0) return null
  return Math.floor(page)
}

/**
 * Convert an anchor's PDF rect to viewer document-space (origin top-left,
 * same units as `pageIntrinsic`). Returns null when the rect is missing
 * or malformed.
 */
export function anchorRectToDocumentRect(
  anchor: FieldAnchor | null | undefined,
  pageIntrinsic: PageDimensions,
): Rect | null {
  if (!anchor) return null
  const rect = anchor.rect
  if (!rect || rect.length < 4) return null
  const [rx1, ry1, rx2, ry2] = rect
  if (
    typeof rx1 !== 'number' ||
    typeof ry1 !== 'number' ||
    typeof rx2 !== 'number' ||
    typeof ry2 !== 'number'
  ) {
    return null
  }
  if (![rx1, ry1, rx2, ry2].every(Number.isFinite)) return null

  const pageHeight = pageIntrinsic.height
  const left = Math.min(rx1, rx2)
  const right = Math.max(rx1, rx2)
  // PDF y axis grows UP from the bottom; subtract from page height to map
  // into a top-left origin space matching `intrinsic`.
  const top = pageHeight - Math.max(ry1, ry2)
  const bottom = pageHeight - Math.min(ry1, ry2)

  const width = right - left
  const height = bottom - top
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null
  if (width <= 0 || height <= 0) return null

  return { x: left, y: top, width, height }
}

/**
 * Convert a viewer document-space rect (top-left origin) back into the
 * persisted PDF rect shape (bottom-left origin).
 */
export function documentRectToAnchorRect(
  rect: Rect,
  pageIntrinsic: PageDimensions,
): [number, number, number, number] {
  const left = rect.x
  const right = rect.x + rect.width
  const top = pageIntrinsic.height - rect.y
  const bottom = pageIntrinsic.height - (rect.y + rect.height)
  return [
    roundAnchorCoord(left),
    roundAnchorCoord(bottom),
    roundAnchorCoord(right),
    roundAnchorCoord(top),
  ]
}

function roundAnchorCoord(value: number): number {
  return Math.round(value * 100) / 100
}
